import { create } from "zustand";
import { loginUser, logoutUser } from "../apis/user/auth";
import { securityUtils } from "../utils/securityUtils";
import { persist, createJSONStorage } from "zustand/middleware";

interface User {
  email: string;
  name: string | null;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  hydrationPending: boolean;
}

interface AuthStore extends AuthState {
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => void;
  setAuth: (user: User | null, token: string | null) => void;
  getAccessToken: () => string | null;
  setHydrationPending: (pending: boolean) => void;
}

// 세션 스토리지에서 사용자 정보를 안전하게 저장/복원
const USER_INFO_KEY = "user_info";

const saveUserInfo = (user: AuthState["user"]) => {
  if (user) {
    sessionStorage.setItem(USER_INFO_KEY, JSON.stringify(user));
  }
};

const loadUserInfo = (): AuthState["user"] => {
  const savedInfo = sessionStorage.getItem(USER_INFO_KEY);
  try {
    return savedInfo ? JSON.parse(savedInfo) : null;
  } catch {
    return null;
  }
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      accessToken: null,
      hydrationPending: true,

      // Actions
      setAuth: (user, token) =>
        set({
          user,
          accessToken: token,
          hydrationPending: false,
        }),

      setHydrationPending: (pending) => set({ hydrationPending: pending }),

      login: async (credentials) => {
        try {
          const response = await loginUser(credentials);

          if (!response.data) {
            console.error("사용자 정보가 없습니다.");
            throw new Error("사용자 정보가 없습니다.");
          }

          const user = {
            email: response.data.email,
            name: response.data.name || "",
          };

          set({
            user,
            accessToken: response.data.accessToken,
            hydrationPending: false, // 로그인 성공 시 hydrationPending을 false로 설정
          });

          saveUserInfo(user);
        } catch (error) {
          throw error;
        }
      },

      logout: async () => {
        const accessToken = get().accessToken;
        if (!accessToken) {
          throw new Error("Access token is not available");
        }

        const response = await logoutUser(accessToken);

        set({
          user: null,
          accessToken: null,
          hydrationPending: false,
        });

        sessionStorage.removeItem(USER_INFO_KEY);
        securityUtils.secureLogout();
      },

      getAccessToken: () => get().accessToken,
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => sessionStorage),
      onRehydrateStorage: () => (state) => {
        // hydration이 완료되면 hydrationPending을 false로 설정
        if (state) {
          state.setHydrationPending(false);
        }
      },
    }
  )
);
